"use client"

import { useState, useEffect } from "react"
import { useWallet } from "@solana/wallet-adapter-react"
import { Connection, PublicKey } from "@solana/web3.js"
import { IRMASubscriptionClient } from "@/lib/irma-client-browser"

interface StakingDashboardProps {
  connection: Connection
  irmaMint: string
}

const TIER_NAMES = ["Bronze", "Silver", "Gold", "Platinum"]

export function StakingDashboard({ connection, irmaMint }: StakingDashboardProps) {
  const { publicKey, signTransaction } = useWallet()
  const [stakes, setStakes] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (publicKey) loadStakes()
  }, [publicKey])

  const loadStakes = async () => {
    if (!publicKey) return
    setLoading(true)
    try {
      const wallet = {
        publicKey,
        signTransaction: async (tx: any) => {
          if (!signTransaction) throw new Error("Wallet not available")
          return await signTransaction(tx)
        },
      }
      const client = new IRMASubscriptionClient(connection, wallet)
      const allAccounts = await client.getAllStakingAccounts(publicKey)
      const activeStakes = allAccounts
        .filter((s: any) => s.isActive)
        .map((s: any) => {
          const tierMap: Record<string, number> = { bronze: 0, silver: 1, gold: 2, platinum: 3 }
          const tierKey = s.stakeTier ? Object.keys(s.stakeTier)[0] : "bronze"
          return { ...s, tier: tierMap[tierKey] ?? 0 }
        })
      setStakes(activeStakes)
    } catch (err: any) {
      console.error("Error loading stakes:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleUnstake = async (stakeNonce: number, tier: number, early: boolean) => {
    if (!publicKey || !signTransaction) return
    if (early && !confirm("Are you sure you want to unstake early? You will incur a penalty.")) return
    try {
      const wallet = { publicKey, signTransaction: async (tx: any) => await signTransaction(tx) }
      const client = new IRMASubscriptionClient(connection, wallet)
      const mintPubkey = new PublicKey(irmaMint)
      const tx = await client.unstake(publicKey, tier, early, mintPubkey, stakeNonce)
      await connection.confirmTransaction(tx, "confirmed")
      alert(`Unstake successful! Transaction: ${tx}`)
      await loadStakes()
    } catch (err: any) {
      alert(`Unstake failed: ${err.message}`)
    }
  }

  const handleClaimRewards = async (tier: number, nonce: number) => {
    if (!publicKey || !signTransaction) return
    try {
      const wallet = { publicKey, signTransaction: async (tx: any) => await signTransaction(tx) }
      const client = new IRMASubscriptionClient(connection, wallet)
      const mintPubkey = new PublicKey(irmaMint)
      const tx = await client.claimRewards(publicKey, tier, mintPubkey, nonce)
      await connection.confirmTransaction(tx, "confirmed")
      alert(`Rewards claimed! Transaction: ${tx}`)
      await loadStakes()
    } catch (err: any) {
      alert(`Claim failed: ${err.message}`)
    }
  }

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full max-w-[480px] mx-auto">
      <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
        <div className="px-1 mb-3">
          <div className="text-lg font-bold text-white mb-1">My Stakes</div>
          <div className="text-sm text-[#8B949E] font-medium">Manage your active stakes</div>
        </div>
        {children}
      </div>
    </div>
  )

  if (!publicKey) {
    return (
      <Shell>
        <div className="bg-[#1C1F26] rounded-2xl p-8 text-center">
          <p className="text-[#8B949E] text-lg">Please connect your wallet to view staking dashboard</p>
        </div>
      </Shell>
    )
  }

  if (loading) {
    return (
      <Shell>
        <div className="bg-[#1C1F26] rounded-2xl p-8 flex items-center justify-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#25e4e8] border-t-transparent" />
          <p className="text-[#8B949E] text-lg">Loading stakes...</p>
        </div>
      </Shell>
    )
  }

  if (stakes.length === 0) {
    return (
      <Shell>
        <div className="bg-[#1C1F26] rounded-2xl p-8 text-center">
          <p className="text-[#8B949E] text-lg">No active stakes found</p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="space-y-3">
        {stakes.map((stake, idx) => {
          const tierName = TIER_NAMES[stake.tier]
          const amount = Number(stake.amountStaked) / 1e9
          const lockEnd = new Date(Number(stake.lockEnd) * 1000)
          const isLocked = lockEnd > new Date()
          const rewardsEarned = Number(stake.rewardsEarned) / 1e9
          const rewardsClaimed = Number(stake.rewardsClaimed) / 1e9
          const unclaimedRewards = rewardsEarned - rewardsClaimed

          return (
            <div key={idx} className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#25e4e8] flex items-center justify-center text-black font-bold text-sm">
                    {tierName.charAt(0)}
                  </div>
                  <div>
                    <span className="font-bold text-lg text-white">{tierName}</span>
                    <p className="text-sm text-[#8B949E]">{amount.toFixed(4)} IRMA</p>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-green-500/20 text-green-400 border border-green-500/30'
                }`}>
                  {isLocked ? "Locked" : "Unlockable"}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <span className="text-xs text-[#8B949E]">Lock End:</span>
                  <p className="font-semibold text-white text-sm">{lockEnd.toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-xs text-[#8B949E]">Total Rewards:</span>
                  <p className="font-semibold text-[#25e4e8] text-sm">{rewardsEarned.toFixed(4)} IRMA</p>
                </div>
              </div>

              {unclaimedRewards > 0 && (
                <div className="bg-[#25e4e8]/10 border border-[#25e4e8]/20 rounded-xl p-3 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#25e4e8] font-medium">Unclaimed Rewards:</span>
                    <span className="font-bold text-[#25e4e8]">{unclaimedRewards.toFixed(4)} IRMA</span>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {unclaimedRewards > 0 && (
                  <button
                    onClick={() => handleClaimRewards(stake.tier, Number(stake.nonce))}
                    className="flex-1 bg-[#25e4e8] hover:bg-[#1dd1d5] text-black font-bold text-sm py-2 px-4 rounded-lg transition active:scale-[0.98]"
                  >
                    Claim Rewards
                  </button>
                )}
                <button
                  onClick={() => handleUnstake(Number(stake.nonce), stake.tier, isLocked)}
                  className={`flex-1 font-bold text-sm py-2 px-4 rounded-lg transition active:scale-[0.98] ${
                    isLocked ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30' : 'bg-[#25e4e8] hover:bg-[#1dd1d5] text-black'
                  }`}
                >
                  {isLocked ? "Unstake Early" : "Unstake"}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </Shell>
  )
}
